import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

export type AgentStatus = "available" | "coming_soon";

export type AgentPlan = 'starter' | 'core' | 'pro';

export interface Agent {
  key: string;
  name: string;
  role: string;
  tagline: string;
  description: string;
  capabilities: string[];
  status: AgentStatus;
  plan: AgentPlan;
  configurable: boolean;
  enabled: boolean;
  locked: boolean;
  required_plan_label: string;
}

interface AgentListResponse {
  agents: Agent[];
}

/**
 * Service for the hireable agents feature. Agents are enabled workspace-wide
 * (tenant-scoped) by a recruiter from the Agents page.
 */
export const useAgentsService = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;

  const getAuthHeader = useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      },
    });
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const getAgents = useCallback(async (): Promise<Agent[]> => {
    const authHeader = await getAuthHeader();
    const response = await fetch(`${apiUrl}/api/agents`, {
      method: "GET",
      headers: { ...authHeader },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail ?? error.message ?? "Failed to load agents");
    }
    const data: AgentListResponse = await response.json();
    return data.agents;
  }, [apiUrl, getAuthHeader]);

  const setAgentEnabled = useCallback(
    async (agentKey: string, enabled: boolean): Promise<boolean> => {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/agents/${agentKey}/enable`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ enabled }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          error.detail ?? error.message ?? "Failed to update agent"
        );
      }
      const data: { agent_key: string; enabled: boolean } =
        await response.json();
      return data.enabled;
    },
    [apiUrl, getAuthHeader]
  );

  return { getAgents, setAgentEnabled };
};
